/**
 * **各域接口的薄封装** —— 只负责"路径 + 契约类型"，不含任何业务判断。
 *
 * 🔴 三个纪律：
 * 1. **类型一律取 `@jiaying/shared` 的契约**，不在端侧重声明一遍（重声明 = 契约漂移的起点）；
 * 2. **不在这里算任何金额** —— 余额、合计、分组计数全部用服务端下发字段（规格 §22.2 派生优先）；
 * 3. 每个函数**只对应一条真实存在的服务端路由**（写错的路径会在联调时立刻 404，不会静默）。
 */

import type {
  AccountDeletionView,
  AccountDeletedView,
  AccountLedgerView,
  AdjustBalanceRequest,
  AdjustBalanceResult,
  UsePassRequest,
  UsePassResult,
  AccountListResult,
  AccountView,
  AuthUserView,
  CategoryListResult,
  ContributionView,
  ContributionWindow,
  CreateAccountRequest,
  CreateBudgetRequest,
  CreatePolicyRequest,
  CreateGoalRequest,
  CreditBillsResult,
  CreditBillLegsView,
  // 🆕 批 106-B：还款优惠与已还三态（契约类型，端侧不重声明）
  CreditRepayRequest,
  CreditRepayResult,
  CreditWaiverView,
  CreateCreditWaiverRequest,
  UpdateCreditWaiverRequest,
  // 🆕 批 99 B2：S 域（投资）—— 契约类型，端侧不重声明
  BatchUpdatePriceRequest,
  CreateTradeRequest,
  IncomeResultView,
  InvestmentOverviewView,
  InvestmentReconcileView,
  InvestmentTradesView,
  ReconcileQuery,
  RecordInvestmentIncome,
  RevokeTradeResultView,
  TradeResultView,
  UpdateHoldingPriceRequest,
  UpdatePriceResult,
  ImportBatchListResult,
  PaydayCandidateListResult,
  PaydayConfirmResultView,
  PaydayHistoryResult,
  PaydayPreviewView,
  ImportCommitRequest,
  ImportCommitResultView,
  ImportPreviewResult,
  ImportRevokedView,
  ImportRuleDeletedView,
  ImportRuleListResult,
  ImportRuleView,
  UpdateImportRuleRequest,
  CreateScheduleRequest,
  CreateTransactionRequest,
  BatchCreateBudgetRequest,
  BudgetBatchCreateResult,
  BudgetCardView,
  BudgetDeletedView,
  BudgetListResult,
  BudgetSuggestionView,
  BudgetPeriod,
  CurveRange,
  LedgerMemberView,
  InviteView,
  JoinPreviewView,
  JoinResultView,
  JoinStrategy,
  LedgerDissolvedView,
  MemberRemovedView,
  ContraTimelineView,
  LoanProgressListResult,
  LoanScheduleSummaryView,
  PayLoanRequest,
  PayLoanResultView,
  PrepayLoanRequest,
  PrepayLoanResultView,
  ReimburseConfirmRequest,
  ReimbursePreviewRequest,
  ReimbursePreviewView,
  ReimburseResultView,
  // 🆕 批 92：R 域（重大资产）—— 请求体与视图一律取契约
  AdjustValuationRequest,
  AppendInvestmentRequest,
  CreateMajorAssetRequest,
  DisposeMajorAssetRequest,
  MajorAssetActionView,
  MajorAssetDeletedView,
  MajorAssetDetailView,
  MajorAssetListResult,
  PurchaseMajorAssetRequest,
  RecordIncomeRequest,
  SetMajorAssetLoansRequest,
  UpdateMajorAssetRequest,
  SettleContraRequest,
  SettleContraResultView,
  LedgerSummary,
  NetWorthCurveResponse,
  NetWorthResponse,
  Paged,
  PolicyDeletedView,
  GoalContributionListResult,
  GoalDeletedView,
  GoalListResult,
  GoalMoveRequest,
  GoalMoveResultView,
  GoalView,
  PolicyListResult,
  PolicyView,
  PreferencesView,
  ScheduleDeletedView,
  ScheduleListResult,
  ScheduleView,
  SettleScheduleRequest,
  SettleScheduleResultView,
  UpdateTransactionRequest,
  UpdatePreferencesRequest,
  ReportResponse,
  ReminderDoneResultView,
  ReminderListResponse,
  ReminderMarkResultView,
  ReminderReadRequest,
  RestorePreviewView,
  RestoreRequest,
  RestoreResultView,
  RollbackSnapshotRequest,
  SnapshotListView,
  SpendableResponse,
  // 🆕 批 130：未来 30 天现金流覆盖（J 域首页 · 与 SpendableResponse 是两套口径）
  CashCoverageResponse,
  TagListView,
  // 🆕 批 107（K3/K11）：标签创建（同名复用）· 批量三接口 · 示例数据标记
  TagView,
  BulkPatchRequest,
  BulkPatchResultView,
  BulkDeleteRequest,
  BulkMutationResultView,
  // 🆕 批 107（K1）：手动资产 / 负债
  CreateNetWorthItemRequest,
  NetWorthItemDeletedView,
  NetWorthItemListResult,
  NetWorthItemView,
  UpdateNetWorthItemRequest,
  TransactionDetailView,
  // 🆕 批 141：「沿用上月」的响应外层（`{ item: LastSalaryView | null }`）
  LastSalaryResult,
  TransactionRowView,
  UpdateAccountRequest,
  UpdatePolicyRequest,
  UpdateGoalRequest,
  UpdateScheduleRequest,
  UpcomingResponse,
  // 🆕 信用卡分期（`DR-060` · 批 104）
  CreateInstallmentRequest,
  InstallmentCreatedResult,
  InstallmentDeletedResult,
  InstallmentListResult,
  InstallmentPaidResult,
  PayInstallmentRequest,
  // 🆕 自建分类（`DR-062` · 批 112-A）
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CategoryMutationResult,
} from '@jiaying/shared';
import { api, apiUpload, apiWithToken } from './http.js';

// ─────────────────────────── A 域：认证 ───────────────────────────

export interface RegisterResponse {
  token: string;
  expiresAt: number;
  user: AuthUserView;
  /** 🔴 注册时**必然**新建的默认账本 —— 省一次往返（`contract/auth.ts`） */
  ledgerId: string;
}

export interface LoginResponse {
  token: string;
  expiresAt: number;
  user: AuthUserView;
}

/** 注册（同时建好默认账本 3+9 = 12 个起始账户） */
export function register(json: {
  email: string;
  password: string;
  name: string;
  ledgerName: string;
}): Promise<RegisterResponse> {
  return api<RegisterResponse>('/api/auth/register', { method: 'POST', json });
}

/** 登录 —— ⚠️ 响应**没有** `ledgerId`（当前账本由客户端决定，见 `session.ts`） */
export function login(json: { email: string; password: string }): Promise<LoginResponse> {
  return api<LoginResponse>('/api/auth/login', { method: 'POST', json });
}

// ─────────────────────────── L 域：账本 ───────────────────────────

/** 我参与的账本（跨账本入口 / 账本切换器） */
export function listLedgers(): Promise<LedgerSummary[]> {
  return api<LedgerSummary[]>('/api/ledgers');
}

/**
 * 🔴 **登录链路专用**：带显式 token 取账本列表（此刻会话尚未写入）。
 * 见 `http.ts` 的 `apiWithToken` 说明 —— 业务代码**不要**用这个。
 */
export function fetchLedgerListWithToken(token: string): Promise<LedgerSummary[]> {
  return apiWithToken<LedgerSummary[]>('/api/ledgers', token);
}

/**
 * 账本成员名单（L 域 · `AC-L6.1`：owner 与 member **逐行相同**）。
 *
 * 🔴 顶栏头像堆叠与设置页「成员」面板的**唯一数据源** —— 两处都读这一份，
 * 不各自再写一个"取成员"的查询（那会让成员名单在两处漂移）。
 */
export function listLedgerMembers(): Promise<LedgerMemberView[]> {
  return api<LedgerMemberView[]>('/api/ledgers/members');
}

/**
 * 账本改名（🔴 **仅 owner**；接口归 L 域、UI 在 N 域「账本设置」）。
 *
 * ⚠️ 路径是 `PATCH /api/ledgers`（**根**，不是 `/ledgers/rename`）——
 * 服务端把「改这本账本」表达为对账本资源本身发 `PATCH`（`collaboration.controller.ts` 文件头），
 * 入参里**没有** `ledgerId`：目标账本只能由 `X-Ledger-Id` 经守卫推导（架构 §6.2 铁律）。
 */
export function renameLedger(name: string): Promise<LedgerSummary> {
  return api<LedgerSummary>('/api/ledgers', { method: 'PATCH', json: { name } });
}

/**
 * 🆕 切换账本本币（🔴 **仅 owner** · `DR-068` · 批 121 / K4）。
 *
 * 🔴 服务端**零数据迁移**：只改 `ledgers.base_currency` 一行。三条连带各自生效的方式：
 * ① 现值跟随（`composeNetWorth` 入参变即变）② 报表重算（`report.controller` 吃 `ctx.baseCurrency`）
 * ③ 曲线**不重算**（快照点自带写入当日的币种）。
 * ⇒ **端侧不需要做任何"换算"**，只需要在成功后失效缓存（见 `invalidateAfterBaseCurrencyChange`）。
 *
 * ⚠️ 与当前本币相同 ⇒ **400**（不是幂等 200）—— 见 `DR-068` §3：同值意味着下拉读到的是旧缓存，
 * 那是真实缺陷，不该被静默掉。
 */
export function updateLedgerBaseCurrency(baseCurrency: string): Promise<LedgerSummary> {
  return api<LedgerSummary>('/api/ledgers/base-currency', {
    method: 'PATCH',
    json: { baseCurrency },
  });
}

// ─────────────────── L 域：协作管理（批 89 · 邀请 / 成员 / 加入 / 解散） ───────────────────
//
// 🔴 **权限与校验一律在服务端**（`collaboration.controller.ts` 的路由 × 权限表）：
// - 邀请码两条是 `@RequireOwner(InviteMember)`；成员满 4 人 ⇒ **409**（状态冲突，不是 403）
// - 移除 / 解散要**二次输入名称**，服务端校验相等（不等 ⇒ 400）
// - `join/preview` 与 `join` 是 `@NoLedger()`（调用者还不是目标账本成员）
//   ⇒ 本文件仍照常发 `X-Ledger-Id`：`LedgerGuard` 对 `@NoLedger()` **直接放行**，
//     忽略该头（实测 `ledger.guard.ts:49`）。端侧**不为它开特例** ——
//     「账本上下文只有一个来源」这条纪律比"少发一个头"重要。

/** 当前有效邀请码（`null` = 还没生成 —— 正常态，不是 404） */
export function currentLedgerInvite(): Promise<InviteView | null> {
  return api<InviteView | null>('/api/ledgers/invites/current');
}

/** 生成 / 重新生成邀请码（🔴 **单码模型**：重新生成即作废旧码） */
export function createLedgerInvite(): Promise<InviteView> {
  return api<InviteView>('/api/ledgers/invites', { method: 'POST' });
}

/** 转让所有权（响应 = **更新后的成员列表**，让"谁变成了 owner"可断言） */
export function transferOwnership(toUserId: string): Promise<LedgerMemberView[]> {
  return api<LedgerMemberView[]>('/api/ledgers/transfer', {
    method: 'POST',
    json: { toUserId },
  });
}

/** 移除成员（= 一次单人拆分；需二次输入成员姓名，服务端校验） */
export function removeLedgerMember(
  userId: string,
  confirmMemberName: string,
): Promise<MemberRemovedView> {
  return api<MemberRemovedView>('/api/ledgers/members/remove', {
    method: 'POST',
    json: { userId, confirmMemberName },
  });
}

/** 解散账本（= 按账户归属拆分；需二次输入账本名称，服务端校验） */
export function dissolveLedger(confirmLedgerName: string): Promise<LedgerDissolvedView> {
  return api<LedgerDissolvedView>('/api/ledgers/dissolve', {
    method: 'POST',
    json: { confirmLedgerName },
  });
}

/** 加入家庭 · 冲突预览（**只读零落库** —— `AC-L5.1`） */
export function previewJoinLedger(code: string, sourceLedgerId?: string): Promise<JoinPreviewView> {
  return api<JoinPreviewView>('/api/ledgers/join/preview', {
    method: 'POST',
    // 🔴 可选键**剔键**而不是发 `undefined`（铁律 63）
    json: sourceLedgerId === undefined ? { code } : { code, sourceLedgerId },
  });
}

/** 加入家庭 · 执行（🔴 `strategy` 必填 —— `merge` / `discard`，不可跳过 `AC-L5.4`） */
export function joinLedger(
  code: string,
  strategy: JoinStrategy,
  sourceLedgerId?: string,
): Promise<JoinResultView> {
  return api<JoinResultView>('/api/ledgers/join', {
    method: 'POST',
    json: sourceLedgerId === undefined ? { code, strategy } : { code, strategy, sourceLedgerId },
  });
}

/**
 * 贡献视图（L 域 L3 · `AC-L3.6`–`L3.10`）—— ✅ **全员可见**（`AC-L6.1`）。
 *
 * ## 🔴 端侧**只转发窗口枚举**，一个日期都不算
 *
 * `from` / `to` 由服务端经账本时区 + `nucleus/calendar.ts` 算好下发。端侧若自己算
 * "近 3 月是几号"，Web 与小程序就会在跨月/跨年那天差一天（契约里已明写这条）。
 * ⇒ 本函数只把 `window` 拼进 query，其余全用服务端结果。
 *
 * ## 🔴 `window` 省略时**不拼 query**（不是拼 `window=thisMonth`）
 *
 * 契约层刻意**没有**给 `window` 设 `.default()`（"默认本月"是服务端口径，不是契约形态）。
 * 端侧补上这个值 = 把这个口径抄到第二处。省略即让服务端用它的缺省。
 *
 * ## ⚠️ 返回的 `expense` / `totalExpense` **不得**用于任何端侧再聚合
 *
 * `AC-L3.6` 明禁第二份聚合实现 —— 两桶之和 ≡ 总支出是**服务端算好的不变量**，
 * 端侧只渲染，不重算（也不要做"我 + Ta 是否等于 total"的校验：那本身就是第二份求和）。
 */
export function getContribution(window?: ContributionWindow): Promise<ContributionView> {
  const qs = window === undefined ? '' : `?window=${encodeURIComponent(window)}`;
  return api<ContributionView>(`/api/ledgers/contribution${qs}`);
}

// ─────────────────────────── B 域：账户 ───────────────────────────

/**
 * 全部账户（⚠️ 含 `hidden` 的社保账户 —— 由端侧决定是否折叠）
 *
 * 🔴 **实测踩过两次，教训要连着看**：
 *
 * | 第几次 | 当时的写法 | 后果 |
 * |---|---|---|
 * | ⑳ 批（本文件的上一版） | 返回类型写成 `AccountView[]`（裸数组） | 运行时 `.items` 是 `undefined`；`api<T>` 的泛型是**自己填的**，TS **不报错** |
 * | ㉑ 批（本批复核） | 改成 `Paged<AccountView>` | 🔴 **仍然错** —— `/api/accounts` **刻意不分页** |
 *
 * 真实形状是 `AccountListResult = { items, total }`（**没有** `page` / `pageSize` / `hasMore`），
 * 见 `account.controller.ts` 的注释：「账户是**有界小集合**，端侧选择器要全量，
 * 分页反而会制造'搜不到某个账户'的 bug」。
 *
 * ⇒ 两次都错在**同一个根因**：拿"别的列表接口的形状"套过来，而没去看**这个 controller 的返回类型**。
 * 纪律：**每个列表接口的类型必须逐一对照自己的 controller**（`Paged<T>` 目前只有流水在用）。
 */
export function listAccounts(): Promise<AccountListResult> {
  return api<AccountListResult>('/api/accounts');
}

/**
 * 🆕 本账本**全部信用卡**的当期 / 未出 / 近 N 期账单（批次 `94` ·
 * `GET /api/accounts/credit-bills?months=3`）。
 *
 * 🔴 数字**全部由服务端算**（账期窗口是半开区间 + 月末 clamp，端侧重算必然漂移）——
 * 本函数只负责取，页面只负责画（B 域 PRD §5.2.2「必须与银行账单对得上」）。
 *
 * ⚠️ 路径是 `/credit-bills`（字面量段），服务端已把它声明在 `:id` 之前 ——
 * 否则会被当成 `accountId = "credit-bills"` 而 404（铁律 90 的同型坑）。
 */
export function listCreditBills(months = 6): Promise<CreditBillsResult> {
  return api<CreditBillsResult>(`/api/accounts/credit-bills?months=${String(months)}`);
}

/**
 * 🆕 **某一期账单的明细**（批 `106-A` · `GET /api/accounts/credit-bills/legs`）。
 *
 * 🔴 **只传"看哪一档"，不传日期**：窗口（含「账单日 31 在短月按月末」这类边界）
 * 是服务端内核的事 —— 端侧若自行按月份过滤，明细就会与卡面的本期应还错开一期
 * （`AC-B2.29` · B 域 PRD §5.2.2「必须与银行账单对得上」）。
 *
 * 🔴 **路由三段、`accountId` 走 query**：与 `GET /api/accounts/:id` 不冲突，
 * 也避免"账单 ID（其实是账单日）被当成账户 id"的语义混淆。
 *
 * @param period `current` / `unbilled` / 往期的**账单日** `YYYY-MM-DD`
 */
export function getCreditBillLegs(
  accountId: string,
  period: string,
  limit = 300,
): Promise<CreditBillLegsView> {
  const q = new URLSearchParams({ accountId, period, limit: String(limit) });
  return api<CreditBillLegsView>(`/api/accounts/credit-bills/legs?${q.toString()}`);
}

/**
 * 🆕 **信用卡还款**（批 `106-B` · `POST /api/accounts/credit-bills/repay`）。
 *
 * 🔴 **单一原子接口**（`AC-B2.49`）：实付转账 + 优惠落腿 + 写 waiver 表一个事务完成。
 * 端侧**不得**拆成「先 createTransaction 再 createWaiver」两次 POST —— 中途失败会留下
 * "转了钱没记优惠"的半截状态（批 104「分期动作非原子」的教训）。
 *
 * 🔴 `amount` 是**净额法**实付（已扣优惠）—— 契约层锁定，端侧不再做加减。
 */
export function repayCreditBill(body: CreditRepayRequest): Promise<CreditRepayResult> {
  return api<CreditRepayResult>('/api/accounts/credit-bills/repay', { method: 'POST', json: body });
}

/** 🆕 单独记一笔优惠（批 `106-B` · `AC-B2.50` · 账单明细页「记优惠」） */
export function createCreditWaiver(body: CreateCreditWaiverRequest): Promise<CreditWaiverView> {
  return api<CreditWaiverView>('/api/accounts/credit-bills/waivers', { method: 'POST', json: body });
}

/** 🆕 改优惠（批 `106-B` · `AC-B2.51`：服务端同事务改腿金额与表行） */
export function updateCreditWaiver(
  waiverId: string,
  body: UpdateCreditWaiverRequest,
): Promise<CreditWaiverView> {
  return api<CreditWaiverView>(`/api/accounts/credit-bills/waivers/${encodeURIComponent(waiverId)}`, {
    method: 'PATCH',
    json: body,
  });
}

/** 🆕 删优惠（批 `106-B` · `AC-B2.51`：服务端同事务软删交易与表行） */
export function deleteCreditWaiver(waiverId: string): Promise<AccountDeletedView> {
  return api<AccountDeletedView>(`/api/accounts/credit-bills/waivers/${encodeURIComponent(waiverId)}`, {
    method: 'DELETE',
  });
}

/**
 * 🆕 校准账户余额（`DR-056` · 批次 96）。
 *
 * 🔴 只传**目标余额**，不传差额：差额由服务端用**当前余额**算
 * （端侧展示的可能已过期 ⇒ 让端侧算会把并发记的账冲掉）。
 */
export function adjustAccountBalance(
  accountId: string,
  body: AdjustBalanceRequest,
): Promise<AdjustBalanceResult> {
  return api<AdjustBalanceResult>(`/api/accounts/${accountId}/adjust-balance`, {
    method: 'POST',
    json: body,
  });
}

/**
 * 🆕 预付卡「用一次」（`DR-057` D4.3 · 批次 97）。
 *
 * 🔴 **不传金额**：单次金额是账户属性，让端侧传就等于允许每次扣不同价。
 */
export function useAccountPass(
  accountId: string,
  body: UsePassRequest = {},
): Promise<UsePassResult> {
  return api<UsePassResult>(`/api/accounts/${accountId}/use-pass`, {
    method: 'POST',
    json: body,
  });
}

/**
 * 🆕 一个账户的**流水明细**（批次 `95` · `GET /api/accounts/:id/ledger`）。
 *
 * 往来账户另有 `getContraTimeline()`（带结算行语义）；本函数是**通用**的那一份，
 * 预付 / 待摊 / 信用卡 / 储蓄卡都用它。
 */
export function getAccountLedger(accountId: string, limit = 100): Promise<AccountLedgerView> {
  return api<AccountLedgerView>(
    `/api/accounts/${encodeURIComponent(accountId)}/ledger?limit=${String(limit)}`,
  );
}

/**
 * 🆕 **投资域（S）五条路由** —— 批 `99` B2 接端侧（`DR-059` / PRD v2 §7.2）。
 *
 * 🔴 **一屏只调一次 `getInvestmentOverview()`**：持仓、六个汇总数、三档回报率、年化
 * 全在这一个响应里（`AC-INV13`）—— 端侧**不许**拿持仓自己 `reduce` 出汇总，
 * 否则同一个数字会出现两个真值（接口的与页面的），而对不上时没人知道该信哪个。
 */

/** 概览：持仓台账 + 按币种汇总 + 三档回报率 + 年化（服务端算好，端侧零加总） */
export function getInvestmentOverview(): Promise<InvestmentOverviewView> {
  return api<InvestmentOverviewView>('/api/investments/overview');
}

// ── 🆕 信用卡分期（`DR-060` · 批 104 · B 域 PRD 增补 §6.2）──

/** 分期计划列表（含期次 / 来源 / 未归属金额 / `totalRemainingPrincipal` —— 端侧零加算） */
export function listInstallments(): Promise<InstallmentListResult> {
  return api<InstallmentListResult>('/api/installments');
}

/** 建分期（含 sources 数组；返回计划 + 生成的全部期次） */
export function createInstallment(json: CreateInstallmentRequest): Promise<InstallmentCreatedResult> {
  return api<InstallmentCreatedResult>('/api/installments', { method: 'POST', json });
}

/** 还一期（服务端幂等键 `(planId, periodIndex)`，重复提交回放同一结果） */
export function payInstallment(planId: string, json: PayInstallmentRequest): Promise<InstallmentPaidResult> {
  return api<InstallmentPaidResult>(`/api/installments/${encodeURIComponent(planId)}/pay`, {
    method: 'POST',
    json,
  });
}

/** 删计划（期次级联；已有还款流水 ⇒ 409） */
export function deleteInstallment(planId: string): Promise<InstallmentDeletedResult> {
  return api<InstallmentDeletedResult>(`/api/installments/${encodeURIComponent(planId)}`, {
    method: 'DELETE',
  });
}

/**
 * 买入 / 卖出（赎回、取出、兑付都走 `kind='sell'` —— `DR-059` §12.1）。
 *
 * ⚠️ 金额制品种（理财 / 存款）由端侧先把"金额"折成 `quantityE4` 再传
 * （`nucleus/investment.ts` 的 `amountToQtyE4`），**服务端不收 `amountCents`**。
 */
export function createTrade(json: CreateTradeRequest): Promise<TradeResultView> {
  return api<TradeResultView>('/api/investments/trades', { method: 'POST', json });
}

/** 分红 / 结息 —— 只记收益，不动持仓数量与成本（`AC-INV8`） */
export function createInvestmentIncome(json: RecordInvestmentIncome): Promise<IncomeResultView> {
  return api<IncomeResultView>('/api/investments/incomes', { method: 'POST', json });
}

/**
 * 某持仓的交易记录 —— 🆕 批 `127`（`DR-073`），撤销入口的数据源。
 *
 * 🔴 只返回**有效**行（关联资金流水被软删的不出现）。
 */
export function listInvestmentTrades(holdingId: string): Promise<InvestmentTradesView> {
  return api<InvestmentTradesView>(
    `/api/investments/holdings/${encodeURIComponent(holdingId)}/trades`,
  );
}

/**
 * 撤销一笔买入 / 卖出 —— 🆕 批 `127`（`DR-073`）。
 *
 * 🔴 只允许撤该持仓**最新的一笔**（栈式）；撤中间那笔服务端回 `409` 并指名先撤哪笔。
 */
export function revokeInvestmentTrade(id: string): Promise<RevokeTradeResultView> {
  return api<RevokeTradeResultView>(`/api/investments/trades/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/**
 * 更新现价（单条）—— 🔴 **唯一能改市值的入口**。
 *
 * 不做这个入口，`current_price` 会永久停在迁移快照上 ⇒ 市值与三种回报率全是死数
 * （`DR-059` D6：这属于 P0，不是增强）。
 */
export function updateHoldingPrice(
  holdingId: string,
  json: UpdateHoldingPriceRequest,
): Promise<UpdatePriceResult> {
  return api<UpdatePriceResult>(
    `/api/investments/holdings/${encodeURIComponent(holdingId)}/price`,
    { method: 'PATCH', json },
  );
}

/** 批量改价（页头「批量改价」）—— 路径是字面量 `holdings/price`，不带 id */
export function batchUpdateHoldingPrices(json: BatchUpdatePriceRequest): Promise<UpdatePriceResult> {
  return api<UpdatePriceResult>('/api/investments/holdings/price', { method: 'PATCH', json });
}

/**
 * 🆕 **报表口径对账**（`GET /api/investments/reconcile` · 批 99 B3）。
 *
 * 两条路径各算一遍同一件事：报表（`legs` 按分类聚合）vs 投资域（`realized_pnl`）。
 * 不传 `query` ⇒ 服务端做**全量**对账（不是"最近 N 个月" —— 短窗口会把历史差额藏起来）。
 */
export function getInvestmentReconcile(query: ReconcileQuery = {}): Promise<InvestmentReconcileView> {
  const params = new URLSearchParams();
  if (query.from !== undefined) params.set('from', query.from);
  if (query.to !== undefined) params.set('to', query.to);
  const qs = params.toString();
  return api<InvestmentReconcileView>(`/api/investments/reconcile${qs === '' ? '' : `?${qs}`}`);
}

/**
 * 🆕 本账本**全部**贷款账户的卡面进度（`GET /api/loans/progress` · `AC-Q4.5` · 批 `81`）。
 *
 * 🔴 **为什么不是逐个账户调 `/api/schedules/loan/:id`**：L2 借贷区要一次画 N 张卡
 * （N 张卡 = N 个并发请求 + 每个都带全量 `periods`）⇒ 服务端为此提供**批量投影**
 * （`DR-046` D6）。本函数返回的形状**不含 `periods`**。
 *
 * ⚠️ 形状必须对照 `LoanScheduleController.progress` 的返回类型 —— 不套别的列表接口
 * （上面 `listAccounts` 的注释记了两次踩坑）。
 */
export function listLoanProgress(): Promise<LoanProgressListResult> {
  return api<LoanProgressListResult>('/api/loans/progress');
}

/**
 * 🆕 单笔贷款的**计划表摘要 + 全部期次** —— `GET /api/schedules/loan/:accountId`（批 `80` · 批 `84` 首次接端侧）。
 *
 * 🔴 只在**打开还款弹窗时**取一次（`DR-049` D6）：它带全部 `periods`（≤1200 期），
 * 放进账户列表就是 N+1 + 每张卡都拖一份全量期次。
 *
 * 🔴 `ready === false`（参数不全）是**合法状态**（`DR-044` §5 第 2 项）：`periods` 为空、
 * `notReadyReason` 给原因 —— 端侧按提示引导补参数，**不得当异常抛**。
 */
export function getLoanSchedule(accountId: string): Promise<LoanScheduleSummaryView> {
  return api<LoanScheduleSummaryView>(`/api/schedules/loan/${encodeURIComponent(accountId)}`);
}

/**
 * 🆕 **还本期** —— `POST /api/schedules/loan/:accountId/pay`（`AC-F1.19` · `DR-045` D3）。
 *
 * 🔴 还款是**人工三腿**（本金 `excl=1` + 利息**计入** + 贷款侧 `in` `excl=1`），
 * 全部由服务端构造 —— 端侧**不得自己拆本金/利息**（`AC-Q6.4`）。
 * 逐期 `principal` / `interest` 是**覆盖值**（写回计划表 = 单真相），不传 = 用计划值。
 */
export function payLoanPeriod(accountId: string, json: PayLoanRequest): Promise<PayLoanResultView> {
  return api<PayLoanResultView>(`/api/schedules/loan/${encodeURIComponent(accountId)}/pay`, {
    method: 'POST',
    json,
  });
}

/**
 * 🆕 **提前还款** —— `POST /api/schedules/loan/:accountId/prepay`（`AC-F1.20` / `AC-F1.21`）。
 *
 * | `mode` | 效果 |
 * |---|---|
 * | `shorten` | 每期金额不变、**期数缩短** |
 * | `reduce` | 期数不变、**每期金额下降** |
 * | `settle` | 后续期次**全部作废**，`amount` 必须等于剩余本金（否则 400） |
 */
export function prepayLoan(accountId: string, json: PrepayLoanRequest): Promise<PrepayLoanResultView> {
  return api<PrepayLoanResultView>(`/api/schedules/loan/${encodeURIComponent(accountId)}/prepay`, {
    method: 'POST',
    json,
  });
}

/**
 * 🆕 记一次结算（收回 / 还款 / 核销）—— `POST /api/contra/:accountId/settle`（`DR-047` · 批 `82`）。
 *
 * 🔴 **贷款账户不走这里**（服务端返回 400 指路）：贷款还款要同时翻期次、可能带利息腿、可能重排，
 * 那是 `/api/schedules/loan/:id/pay` / `.../prepay` 的事。
 *
 * ⚠️ 超额**不是错误**（`AC-Q2.6`）：服务端回 `overSettled: true`，端侧只提示。
 */
export function settleContra(accountId: string, json: SettleContraRequest): Promise<SettleContraResultView> {
  return api<SettleContraResultView>(`/api/contra/${encodeURIComponent(accountId)}/settle`, {
    method: 'POST',
    json,
  });
}

/**
 * 🆕 L3 明细（三类行 · 时间倒序）—— `GET /api/contra/:accountId/timeline`（`AC-Q5.1`）。
 *
 * 🔴 服务端**只下发 `pending` 期次**（`AC-Q5.2` 修正 v1 病灶）；端侧不要"补上已还的"。
 *
 * 🆕 `invariant`（`AC-Q5.3`）由服务端算好下发；`null` = 该账户没有分母（非贷款），
 * 原因在 `invariantNotCheckedReason`。🔴 端侧**不自己算**这个不变量。
 */
export function getContraTimeline(accountId: string): Promise<ContraTimelineView> {
  return api<ContraTimelineView>(`/api/contra/${encodeURIComponent(accountId)}/timeline`);
}

/**
 * 🆕 报销**预演**（只读）—— `POST /api/contra/:accountId/reimburse/preview`（`DR-048` · 批 `83`）。
 *
 * 🔴 预演**不写库**（`AC-Q3.6`）；服务端在"输入不合法"时也返回 201 + `issues`（不报 400），
 * 好让端侧能显示"这样填会错在哪"。真正拦截在 {@link confirmReimburse}。
 */
export function previewReimburse(
  accountId: string,
  json: ReimbursePreviewRequest,
): Promise<ReimbursePreviewView> {
  return api<ReimbursePreviewView>(
    `/api/contra/${encodeURIComponent(accountId)}/reimburse/preview`,
    { method: 'POST', json },
  );
}

/**
 * 🆕 报销**落账** —— `POST /api/contra/:accountId/reimburse`（`AC-Q3.1`–`Q3.10`）。
 *
 * 🔴 `allocations` 只在用户真的改过时才发（铁律 63：可选键按需剔键，不发空数组）；
 * 不发 = 服务端按垫付比例自动分摊。
 */
export function confirmReimburse(
  accountId: string,
  json: ReimburseConfirmRequest,
): Promise<ReimburseResultView> {
  return api<ReimburseResultView>(`/api/contra/${encodeURIComponent(accountId)}/reimburse`, {
    method: 'POST',
    json,
  });
}

/**
 * 建账户（`POST /api/accounts`）。
 *
 * 🔴 **成员可用**（B 域 PRD 权限表：`POST /api/accounts` 不需 owner —— 规格 §16
 * 把「建改删账户」开放给 member，v1 的 `@Role('owner')` 收紧是病灶⑧）。
 *
 * 🔴 `ownerId` **缺省交给服务端**（= 当前登录用户）—— 端侧**不自己填当前用户 id**。
 * 端侧若自己填，就等于把"默认归当前用户"这条规则**抄了第二遍**
 * （见 `features/account-draft.ts` 文件头「归属人在新建时的处置」）。
 */
export function createAccount(json: CreateAccountRequest): Promise<AccountView> {
  return api<AccountView>('/api/accounts', { method: 'POST', json });
}

/**
 * 改账户（`PATCH /api/accounts/:id`）。
 *
 * 🔴 **不含 `type` / `ownerId` / `initialBalance`** —— 三样都由契约层刻意排除
 * （理由见 `contract/account.ts` 文件头：换形态要删重建；改归属人仅 owner 走专用路由；
 * `AC-B6.7` 红线禁「直接改账户余额」）。本函数的入参类型就是那个受限的契约。
 *
 * 字段语义 = **传了就替换**；`null` 表示"清空该字段"。
 */
export function updateAccount(id: string, json: UpdateAccountRequest): Promise<AccountView> {
  return api<AccountView>(`/api/accounts/${encodeURIComponent(id)}`, { method: 'PATCH', json });
}

/**
 * 删除账户（`DELETE /api/accounts/:id`）。
 *
 * 🔴 **返回类型走契约层**（`AccountDeletedView`，㊴ 批搬进 `contract/account.ts`）。
 * 端侧**绝不自己写** `{ deleted: true; id: string }` —— 那正是 `PolicyDeletedView` 踩过的坑：
 * 形状只活在服务端 ⇒ 端侧重声明 ⇒ 契约漂移（架构 §6.1「契约唯一源」）。
 *
 * ⚠️ `confirmNonZeroBalance` 由服务端强制：余额非零时必须显式确认，否则 `409`。
 * 端侧的处置是**把服务端文案原样展示**并让用户二次确认，**不在端侧预判余额**
 * （端侧没有权威余额；`balance` 是服务端派生字段，可能已被别的腿改变）。
 */
export function deleteAccount(id: string, confirmNonZeroBalance: boolean): Promise<AccountDeletedView> {
  const qs = confirmNonZeroBalance ? '?confirmNonZeroBalance=true' : '';
  return api<AccountDeletedView>(`/api/accounts/${encodeURIComponent(id)}${qs}`, { method: 'DELETE' });
}

// ─────────────────────────── B 域：分类 ───────────────────────────

/**
 * 分类列表（**L1 + L2 一次给全量** · 不分页 —— 与账户列表同因：选择器要全量）。
 *
 * ## 🔴 三条由**调用方**遵守的约束（都写在 `contract/category.ts` 里）
 *
 * | 约束 | 不遵守会怎样 |
 * |---|---|
 * | **顺序即展示顺序**（服务端已排成"L1 → 紧随其 L2"） | 端侧再排一次 = **第二份排序口径**；调序应改身份层数组 |
 * | 🔴 **`common` 标记直接用，不要自己筛"哪些是常用"** | 端侧硬编常用集会让 Web 与小程序**各有一份**；将来用户自定义常用集时两端都要改（`AC-B4.8`） |
 * | 切换支出/收入**不必重取** | 全量已在手，按 `kind` 过滤即可（多一次往返没有任何收益） |
 */
export function listCategories(): Promise<CategoryListResult> {
  return api<CategoryListResult>('/api/categories');
}

/**
 * 🆕 批 `112-B`：**含已移除**的分类列表（条目带 `removed: true`）。
 *
 * 🔴 为什么是**独立函数**而不是给 `listCategories` 加个参数：
 * 全项目 5 处都写成 `queryFn: listCategories`，react-query 会把 **query context 对象**
 * 当第一个实参传进去 ⇒ 若签名是 `listCategories(includeHidden = false)`，
 * 那个对象恒为真值 ⇒ **所有选择器都静默变成"含已移除"**（`AC-B3.14` 直接破）。
 * 独立函数没有这个入口，调用点必须显式写出来。
 *
 * 两个合法用途（都**不是**"把已移除项混进选择器"）：
 * 1. 流水**分类筛选**（用户要按历史分类筛选）；
 * 2. 🆕 批 `122`：**管理对话框的「含已移除」区**（恢复入口 —— 列表里没有它就无从恢复，`DR-070` D1）。
 *
 * 🔴 **不得**用在录账 / 批量 / 导入 / 计划的任何选择器候选项上。
 */
export function listCategoriesWithRemoved(): Promise<CategoryListResult> {
  return api<CategoryListResult>('/api/categories?includeHidden=1');
}

// ─── 🆕 批 112-A：自建分类的写路径（`DR-062`） ───

/**
 * 建自建分类（`POST /api/categories`）。
 *
 * 🔴 `parentId: null` = 建 **L1**（此时 `kind` 必填）；非空 = 在该 L1 下建 **L2**
 * （此时 `kind` **由服务端强制取父级**，传了也被忽略 —— `AC-B3.5` 不信客户端）。
 */
export function createCategory(json: CreateCategoryRequest): Promise<CategoryMutationResult> {
  return api<CategoryMutationResult>('/api/categories', { method: 'POST', json });
}

/**
 * 改名 / 换图标（`PATCH /api/categories/:id`）。
 *
 * 🔴 系统分类会被服务端 `403` —— 端侧照原样展示服务端给的提示语，**不要自己编文案**
 * （`DR-062` §3 边界 2：提示语原话是「系统分类，用于财务报表口径」）。
 */
export function updateCategory(
  id: string,
  json: UpdateCategoryRequest,
): Promise<CategoryMutationResult> {
  return api<CategoryMutationResult>(`/api/categories/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    json,
  });
}

/**
 * **移除**分类（`DELETE /api/categories/:id`）。
 *
 * 🔴 服务端做的是**软删**（`DR-062` D3）：已记过的流水仍显示该分类名。
 * 所以 UI 文案必须是「从分类列表移除」而不是「删除」。
 */
export function removeCategory(id: string): Promise<CategoryMutationResult> {
  return api<CategoryMutationResult>(`/api/categories/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/**
 * 🆕 批 `122`：**恢复**已移除的分类（`POST /api/categories/:id/restore`）—— `DR-070` · `AC-B3.19`。
 *
 * `removeCategory` 的反路径（`hidden = 1 → 0`）。回执复用 `CategoryMutationResult`（写后的那一行）。
 *
 * 🔴 服务端可能回 `409`，**两条都是正常业务分支、不是 bug**：
 * | 情况 | 提示语（服务端原话，端侧照抄、不要自编） |
 * |---|---|
 * | 大类已被移除 | 「它的大类「餐饮」已被移除，请先恢复大类再恢复它」 |
 * | 自建额度已满 | 「自己添加的一级分类最多 20 个，恢复前请先移除另一个自建一级分类」 |
 *
 * ⚠️ 重复恢复是**幂等成功**（服务端 `DR-070` D6）：双击不会报错，也不会写第二条审计。
 */
export function restoreCategory(id: string): Promise<CategoryMutationResult> {
  return api<CategoryMutationResult>(`/api/categories/${encodeURIComponent(id)}/restore`, {
    method: 'POST',
  });
}

// ─────────────────────────── C 域：流水 ───────────────────────────

/** 记一笔（**全形态单一入口**：1 腿 = 收支，2+ 腿 = 转账/工资条/拆分） */
export function createTransaction(json: CreateTransactionRequest): Promise<TransactionRowView> {
  return api<TransactionRowView>('/api/transactions', { method: 'POST', json });
}

/**
 * 流水列表（**C 域 §5.1**：10 维筛选 + 稳定分页 · 排序 `date DESC, createdAt DESC` 由服务端定）。
 *
 * ## 🔴 `query` 是**拼好的查询串**（含前导 `?`；无参数时传 `''`）
 *
 * 为什么不让本函数自己收一个对象：**「草稿 → 请求」是纯逻辑**，它必须在 spec 里能**不渲染、不出网**
 * 直测（与 `txn-draft.ts` / `account-draft.ts` 同一范式）。⇒ 拼装归 `features/journal-filter.ts` 的
 * `buildJournalQuery()`，本函数只负责出网（`api/` 不承载业务规则）。
 *
 * 🔴 **`pageSize` 由调用方传**（默认 50 = `ListConfig.PageSize`）：不在这里写死第二份 ——
 * "每批 50 笔"是产品口径（§5.1.3），载体是共享层常量。
 */
export function listTransactions(query: string): Promise<Paged<TransactionRowView>> {
  return api<Paged<TransactionRowView>>(`/api/transactions${query}`);
}

/**
 * 流水详情（**抽屉** · `AC-C1.33` / `AC-L3.5`）。
 *
 * 🔴 含 `attribution`（归因三行：记录人 / 归属人 / 时间）—— **服务端算好下发**，
 * 端侧**不得**由 `legs[].accountId` 自己映射归属人（那是同一事实的第二份实现）。
 * ⚠️ 服务端对**已软删**的行也返回详情（否则"状态 → 已删除"筛选项点不开）。
 */
export function getTransaction(id: string): Promise<TransactionDetailView> {
  return api<TransactionDetailView>(`/api/transactions/${encodeURIComponent(id)}`);
}

/**
 * 🆕 **上一笔工资条**（批 141 · 「沿用上月」的数据源）。
 *
 * 🔴 返回的是**整笔腿**，不是"结构摘要" —— 折叠（腿 → 子项）在
 * `features/salary-items.ts` 的 `salaryItemsFromLegs()` 里，服务端不重复实现（铁律 24）。
 *
 * `item: null` = 这个账本还没有可沿用的工资条（**正常结果**，不是错误）。
 */
export function getLastSalary(): Promise<LastSalaryResult> {
  return api<LastSalaryResult>('/api/transactions/last-salary');
}

/**
 * 改一笔（**C 域 §5.1.10「编辑复用记一笔」** · `DR-042` D1 · `AC-C1.41`）。
 *
 * ## 🔴 只发三个字段：`date` / `note` / `legs`
 * 其余（`tagIds` / `status` / `parentId` / `excludeFromStats` / `goalId`）**不发 = 不改**
 * （`UpdateTransactionRequestSchema` 全是 optional）。这样「编辑页没预填某字段」**不会**
 * 造成静默丢失 —— 不发的字段服务端原样保留（`AC-C1.42` 的"逐字段相等"由此成立）。
 *
 * ## 🔴 `legs` 是**整组替换**
 * 必须传**完整腿数组**；只发改动的那一条 ⇒ 其余腿**全丢**（`AC-C1.43`）。
 *
 * ⚠️ 已软删的流水走本接口 ⇒ 服务端 `DELETED`（**删除优先**，架构 §6.5）⇒ 端侧须给恢复引导（`AC-C1.46`）。
 */
export function updateTransaction(
  id: string,
  json: UpdateTransactionRequest,
): Promise<TransactionRowView> {
  return api<TransactionRowView>(`/api/transactions/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    json,
  });
}

/** 标签列表（筛选**维度 5** · `AC-C1.13`）—— 不分页（标签总量极小：系统 3 + 自建） */
export function listTags(): Promise<TagListView> {
  return api<TagListView>('/api/tags');
}

/**
 * 🆕 批 `107`：新建标签（**同名复用** —— 契约明文「同名标签由服务端在创建时复用」）。
 *
 * 用途：示例数据的标记标签「示例数据」按需创建（不进 `SYSTEM_TAG_SEED`，见
 * `contract/tag.ts` 的 `DEMO_DATA_TAG_NAME` 说明）。⇒ 重复点「看示例」不会造出第二条同名标签。
 */
export function createTag(name: string): Promise<TagView> {
  return api<TagView>('/api/tags', { method: 'POST', json: { name } });
}

// ─────────────────────── C 域：批量（`AC-C1.19`–`C1.24`） ───────────────────────

/**
 * 批量改分类 / 改账户 / 加标签 / 去标签（`PATCH /api/transactions/bulk`）。
 *
 * 🔴 **分级授权在服务端**（`AC-C1.20`：结构敏感动作只对**单腿**交易放行）——
 * 端侧**不得**自己造"多腿不能改分类"这类规则，也不得自己预筛让某行"看起来不该被拒"：
 * 拒绝明细（`rejected[].reason`）是**服务端逐条给的**，端侧原样展示（`AC-C1.21` 禁静默失败）。
 */
export function bulkPatchTransactions(body: BulkPatchRequest): Promise<BulkPatchResultView> {
  return api<BulkPatchResultView>('/api/transactions/bulk', { method: 'PATCH', json: body });
}

/**
 * 批量软删（**唯一删除接口**，30 天可恢复 · `AC-C1.23`）。
 *
 * 🔴 `bulk-restore` 是它的逆操作；D 域批次撤销也复用这两个接口（不另建删除逻辑）。
 */
export function bulkDeleteTransactions(ids: readonly string[]): Promise<BulkMutationResultView> {
  return api<BulkMutationResultView>('/api/transactions/bulk-delete', {
    method: 'POST',
    json: { ids: [...ids] } satisfies BulkDeleteRequest,
  });
}

/** 批量恢复（30 天内；恢复时服务端附带一致性提示，**只提示不拦截** · `AC-C1.27`） */
export function bulkRestoreTransactions(ids: readonly string[]): Promise<BulkMutationResultView> {
  return api<BulkMutationResultView>('/api/transactions/bulk-restore', {
    method: 'POST',
    json: { ids: [...ids] },
  });
}

// ─────────────────────────── B 域：保单 ───────────────────────────

/** 保单台账（含"计入净资产的现金价值合计"—— 端侧**不自己加**） */
export function listPolicies(): Promise<PolicyListResult> {
  return api<PolicyListResult>('/api/insurance/policies');
}

/**
 * 建一张保单（字段清单 = B 域 PRD §5.5.5；**被保人是数组**，P0 端侧只放 1 位）。
 *
 * ⚠️ **两种失败必须分开处理**（页面里就是这么做的）：
 * - `400`：字段问题（必填 / 保险期间倒序 / 现金价值 > 保额）—— `message` 是**逐条中文原因**；
 * - 🔴 `409`：**保单号在本账本已存在** —— 这是唯一"改一个字段就能过"的失败，
 *   页面要把它指到保单号输入框上，而不是让人去猜哪句提示对应哪个字段。
 */
export function createPolicy(json: CreatePolicyRequest): Promise<PolicyView> {
  return api<PolicyView>('/api/insurance/policies', { method: 'POST', json });
}

/**
 * 改一张保单 —— 🔴 **整字段替换**（不是打补丁，与 C 域流水编辑同约定；契约注释原文）。
 * ⚠️ 其中 `insureds` 传了就是**整体替换**（P0 端侧只传 1 位，见 `features/policy-draft.ts`）。
 */
export function updatePolicy(id: string, json: UpdatePolicyRequest): Promise<PolicyView> {
  return api<PolicyView>(`/api/insurance/policies/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    json,
  });
}

/** 删一张保单（级联清被保人 —— 服务端同事务处理） */
export function deletePolicy(id: string): Promise<PolicyDeletedView> {
  return api<PolicyDeletedView>(`/api/insurance/policies/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// ─────────────────────────── J 域：净资产 ───────────────────────────

/**
 * 当前净资产（**J 域唯一口径** —— 资产 10 类 / 负债 6 类 + 分桶明细）。
 *
 * 🔴 **端侧不得自己加总**：
 * - 首页 Hero、资产负债页、将来的报表**全部用这一个响应**（J 域 PRD §4.7：首页必须改用 `breakdown`，
 *   v1 的 legacy 派生字段正是"首页一个数、资产负债页另一个数"的来源）；
 * - `notReady` 里的类**不是 0，是"还没有接口"** ⇒ 必须显示「未就绪」，不许当 0 画；
 * - `partial === true` 时两侧合计与 `netWorth` 都只是**下界**，UI 必须标注。
 */
export function getNetWorth(): Promise<NetWorthResponse> {
  return api<NetWorthResponse>('/api/networth');
}

/**
 * 净资产曲线（J 域 J2 · 三档时间范围）。
 *
 * ## 🔴 三条必须由**调用方**遵守的契约约束（都写在 `contract/networth.ts` 里）
 *
 * | 约束 | 不遵守会怎样 |
 * |---|---|
 * | `points` 里**只有库里真实存在的行** —— 无快照的月份**不出现** | 别按"月份连续性"去补点/连假线（`DR-012`：过去的不能预测将来） |
 * | `enoughForCurve === false` ⇒ **必须显示空态「数据积累中」，不得画任何线** | 两点连一条线看起来像"净资产在上涨"，而那只是"数据太少" |
 * | 🔴 **筛选 `origin` 落在绘制层** —— 本接口**会**同时返回 `backfill` 点 | 端侧**只绘 `origin='live'`**；⚠️ **不要**因此要求服务端过滤（那会让数据层永久失去回填行，它们是历史事实的记录） |
 *
 * ⚠️ `range` 是**产品定的三值枚举**（`12m` / `36m` / `all`），越界即 400 —— 不是"传月份数"。
 */
export function getNetWorthCurve(range: CurveRange): Promise<NetWorthCurveResponse> {
  return api<NetWorthCurveResponse>(`/api/networth/curve?range=${encodeURIComponent(range)}`);
}

// ────────────── J 域：手动资产 / 负债（`DR-030` · 批 107 K1） ──────────────

/**
 * 手动资产 / 负债列表（资产页「手动资产 / 手动负债」区块）。
 *
 * 🔴 返回里的 `amount` 是**原币整数分**，`baseCurrency` 是**账本本币**（新建表单的默认币种）——
 * 两者都**直接用**：端侧不折算（`DR-004`）、不自己取本币。
 */
export function listNetWorthItems(): Promise<NetWorthItemListResult> {
  return api<NetWorthItemListResult>('/api/net-worth-items');
}

/**
 * 新增一条（`POST /api/net-worth-items` · 成功 **201**）。
 *
 * 🔴 **没有"类别"字段** —— `kind` 只有资产 / 负债两值，投资理财类在这里**没有可选的格子**
 * （`DR-030`：以录入时禁止替代事后剔除）。房产 / 车辆请记在「重大资产」，避免双计。
 */
export function createNetWorthItem(json: CreateNetWorthItemRequest): Promise<NetWorthItemView> {
  return api<NetWorthItemView>('/api/net-worth-items', { method: 'POST', json });
}

/** 编辑（🔴 不发的键 = 不改；`note: null` = 清空备注） */
export function updateNetWorthItem(
  id: string,
  json: UpdateNetWorthItemRequest,
): Promise<NetWorthItemView> {
  return api<NetWorthItemView>(`/api/net-worth-items/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    json,
  });
}

/** 删除（物理删 —— 它的金额从来没进过任何腿，故不涉及余额回滚） */
export function deleteNetWorthItem(id: string): Promise<NetWorthItemDeletedView> {
  return api<NetWorthItemDeletedView>(`/api/net-worth-items/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/**
 * 🔴 **本月剩余可花**（J 域 J3 · `GET /api/spendable` · 规格 §12 / `DR-010`）。
 *
 * ## 🔴 三条由**调用方**遵守的约束
 *
 * | 约束 | 不遵守会怎样 |
 * |---|---|
 * | **名字只能用 `SPENDABLE_LABEL`**（端侧别自己写字符串） | 名字一分叉就会出现"两个可花"的错觉；规格 §12 明确点名删除 v1 的「本周可安全花」 |
 * | `result.kind = 'noBudget'` ⇒ **不显示任何数字**、只显示引导 | 拿空预算算出的数一定是假的（`AC-J3.8`） |
 * | `result.kind = 'overspent'` ⇒ 显示「已透支 ¥X」，**不截断为 0** | 截断会把"超了"说成"刚好花完"（`AC-J3.9`） |
 *
 * ⚠️ **它不是 `/api/upcoming` 的封装**：两者窗口不同（F2 = 今天 + 30 天 / J3 = 预算窗口），
 * 只是服务端**共用同一份聚合内核**。端侧也**不要**用 `upcoming` 的数字去拼这个卡片 ——
 * 那会让"必付未付"出现第二份口径。
 */
export function getSpendable(): Promise<SpendableResponse> {
  return api<SpendableResponse>('/api/spendable');
}

/**
 * 🆕 批 `130`：**未来 30 天现金流覆盖**（`GET /api/cashflow/coverage`）。
 *
 * 🔴 与 `getSpendable()` 是**两套口径**，不要混：
 * - 那个分母是**预算额度**（"这个月还能花多少"）；
 * - 这个分母是**账户余额**（"下个月付不出得出"）。
 *
 * ⚠️ 窗口固定 30 天（服务端常量，与 F2 日历同一个）—— 本函数**没有参数**，
 * 也不该有：多一个窗口参数就多一个"未来 N 天"的口径。
 */
export function getCashCoverage(): Promise<CashCoverageResponse> {
  return api<CashCoverageResponse>('/api/cashflow/coverage');
}

// ─────────────────────── K 域：报表（K1–K4 · 四段全时间轴） ───────────────────────

/**
 * 报表四段（① 已发生 / ② 现在的健康线 / ④ P1 占位）。
 *
 * 🔴 **② 段的净资产与 ③ 段的预算执行不在此接口里**（见 `contract/report.ts` 文件头）：
 * 它们分别复用 `getNetWorth()` / `listBudgets()` · `getSpendable()` · `getUpcoming()` ——
 * **同源即逐分恒等**，端侧不拼第二份。
 *
 * ⚠️ `from` + `to` **必须同时给**（`AC-K1.2`）：只给其一会被服务端**静默回落**到 `months`
 * ⇒ 端侧**不要构造这种调用**（本函数也不提供"只传一半"的口子）。
 */
export function getReport(params?: { months?: number; from?: string; to?: string }): Promise<ReportResponse> {
  const qs = new URLSearchParams();
  if (params?.months !== undefined) qs.set('months', String(params.months));
  if (params?.from !== undefined && params?.to !== undefined) {
    qs.set('from', params.from);
    qs.set('to', params.to);
  }
  const q = qs.toString();
  return api<ReportResponse>(q === '' ? '/api/report' : `/api/report?${q}`);
}

// ─────────────────────── F 域：周期与现金流（F1 + F2） ───────────────────────

/**
 * 未来必付日历（F2 · `GET /api/upcoming`）。
 *
 * ## 🔴 三条由**调用方**遵守的约束（都写在 `contract/upcoming.ts` 里）
 *
 * | 约束 | 不遵守会怎样 |
 * |---|---|
 * | `items` 里**已逾期未付项也在**（服务端刻意不设窗口下界，`AC-F2.5`） | 端侧若自己按 `daysUntil >= 0` 过滤，就会把「已逾期 N 天」整类藏掉 —— 而"忘了交物业费"正是用户最需要看见的一条 |
 * | `status` / `daysUntil` **用服务端下发的**，不要自己按本地时区算 | 端侧时区与账本时区可能不同 ⇒ 同一条项在两端显示不同日期 |
 * | `fxIncomplete === true` ⇒ **必须标注合计不完整** | 缺汇率的项不计入 `totalCents`，静默显示会让用户以为"合计就是这么少" |
 */
export function getUpcoming(): Promise<UpcomingResponse> {
  return api<UpcomingResponse>('/api/upcoming');
}

/** 周期规则列表（**不分页** —— 与账户列表同处置） */
export function listSchedules(): Promise<ScheduleListResult> {
  return api<ScheduleListResult>('/api/schedules');
}

/** 建周期规则（= 给待缴 / 待摊账户绑规则；账户 : 规则 = 1:1） */
export function createSchedule(json: CreateScheduleRequest): Promise<ScheduleView> {
  return api<ScheduleView>('/api/schedules', { method: 'POST', json });
}

/**
 * 改周期规则（`PATCH /api/schedules/:id`）。
 *
 * 🔴 **「编辑不覆盖」**（`AC-F1.8`）：本接口**不会**改动任何已生成的流水 ——
 * 端侧只需照常失效流水缓存，不要期待"改规则会让历史变"。`accountId` / `kind` **不可改**
 * （入参类型里就没有它们：改绑 = 删旧建新）。
 */
export function updateSchedule(id: string, json: UpdateScheduleRequest): Promise<ScheduleView> {
  return api<ScheduleView>(`/api/schedules/${encodeURIComponent(id)}`, { method: 'PATCH', json });
}

/** 删周期规则（🔴 **只删规则**，已生成流水保留 —— `keptTransactionCount` 用来做提示文案） */
export function deleteSchedule(id: string): Promise<ScheduleDeletedView> {
  return api<ScheduleDeletedView>(`/api/schedules/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** 付款销账（转账 2 腿 + `pending → settled`）—— 返回凭据以便提示"去哪看这笔" */
export function settleSchedule(id: string, json: SettleScheduleRequest): Promise<SettleScheduleResultView> {
  return api<SettleScheduleResultView>(`/api/schedules/${encodeURIComponent(id)}/settle`, {
    method: 'POST',
    json,
  });
}

// ─────────────────────────── G 域：预算 ───────────────────────────

/**
 * 预算列表（**一次请求拿齐一屏**：卡 + 软提示 + 成员维度）。
 *
 * 🔴 **排序由服务端定**（总预算置顶 → 已超 > 接近 > 正常 → 已用% 降序，`AC-G1.29`）
 * —— 端侧**整屏照序渲染，不重排**（重排 = 第二份排序口径）。
 */
export function listBudgets(): Promise<BudgetListResult> {
  return api<BudgetListResult>('/api/budgets');
}

/**
 * 新建预算的三档建议（`AC-G1.15`：**单接口**返回三档）。
 *
 * 🔴 服务端用 **query string** 收参（`categoryId` / `period`），故此处拼串而非发 body。
 * 🔴 某档为 `null` ⇒ 该档**隐藏**（不是显示 0）—— 端侧照 `null` 判，不要 `?? 0`。
 */
export function getBudgetSuggestions(
  categoryId: string,
  period: BudgetPeriod,
): Promise<BudgetSuggestionView> {
  const qs = new URLSearchParams({ categoryId, period }).toString();
  return api<BudgetSuggestionView>(`/api/budgets/suggestions?${qs}`);
}

/** 建预算（`categoryId` 传 `__total__` 即建总预算） */
export function createBudget(json: CreateBudgetRequest): Promise<BudgetCardView> {
  return api<BudgetCardView>('/api/budgets', { method: 'POST', json });
}

/** 批量创建（🔴 已存在的分类**幂等跳过**，结果带 `skipped` 明细 —— 端侧应如实展示跳过原因） */
export function batchCreateBudgets(json: BatchCreateBudgetRequest): Promise<BudgetBatchCreateResult> {
  return api<BudgetBatchCreateResult>('/api/budgets/batch', { method: 'POST', json });
}

/**
 * 改预算（金额 / 启停 / 事件起止）。
 *
 * 🔴 **不含 `categoryId` / `period`**（契约层就没有）—— 要换分类或周期，删旧建新。
 */
export function updateBudget(
  id: string,
  json: { amount?: number; enabled?: boolean; startDate?: string | null; endDate?: string | null },
): Promise<BudgetCardView> {
  return api<BudgetCardView>(`/api/budgets/${encodeURIComponent(id)}`, { method: 'PATCH', json });
}

/** 删预算（`AC-G1.27`：**不影响任何流水与历史**） */
export function deleteBudget(id: string): Promise<BudgetDeletedView> {
  return api<BudgetDeletedView>(`/api/budgets/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ─────────────────────── N 域：设置（用户偏好 · N2 / N3） ───────────────────────

/**
 * 读当前用户的偏好（`user_preferences` · **用户级** · `DR-031`）。
 *
 * 🔴 **无行 ⇒ 服务端给表默认值**（三档「常规」+ 五类全开 + 免打扰 `22:00–07:30`）——
 * 端侧**不写第二份默认值**（那会在"服务端改了默认"时静默漂移）。
 */
export function getPreferences(): Promise<PreferencesView> {
  return api<PreferencesView>('/api/settings/preferences');
}

/**
 * 保存偏好。
 *
 * 🔴 `reminder` 组是**整体替换**（不做字段级合并 —— 见 `contract/setting.ts` 文件头）：
 * 每次都发**完整**的这一组，`typeEnabled` 五个键一个都不能少。
 * 🔴 **空 body（`{}`）⇒ 服务端无操作**。
 */
export function updatePreferences(body: UpdatePreferencesRequest): Promise<PreferencesView> {
  return api<PreferencesView>('/api/settings/preferences', { method: 'PATCH', json: body });
}

// ─────────────────────── H 域：提醒中心（站内兜底通道 · H2） ───────────────────────

/**
 * 站内提醒中心列表（三档分组 + 免打扰状态 + 通道可用性）。
 *
 * 🔴 **站内中心不依赖任何授权**（H 域 §5.2.2）—— 拒绝全部推送也能看到全部提醒。
 * ⚠️ `includeEarlier` 走**字面量字符串**（`'true'` / `'false'`）：
 * 服务端契约刻意不用 `z.coerce.boolean()`（那会把 `'false'` 当 `true`，见 `contract/reminder.ts`）。
 */
export function getReminders(params?: { includeEarlier?: boolean }): Promise<ReminderListResponse> {
  const qs = new URLSearchParams();
  if (params?.includeEarlier !== undefined) {
    qs.set('includeEarlier', params.includeEarlier ? 'true' : 'false');
  }
  const tail = qs.toString();
  return api<ReminderListResponse>(`/api/reminders${tail === '' ? '' : `?${tail}`}`);
}

/**
 * 标记已读。
 *
 * 🔴 `ids` 与 `all` **互斥**（同给 ⇒ `400`）；空 `ids` 且无 `all` ⇒ 无操作
 * （**空数组不等于"全部"**）。⚠️ 传 `ids` 时**合并条要展开**（见 `features/reminder.ts`）。
 */
export function markRemindersRead(json: Pick<ReminderReadRequest, 'ids' | 'all'>): Promise<ReminderMarkResultView> {
  return api<ReminderMarkResultView>('/api/reminders/read', { method: 'POST', json });
}

/**
 * 标记已处理（原型「已还，别再提」）。
 *
 * 🔴 只写 `doneAt`，**不删行**（`§6.1 E5`：关闭 ≠ 删除历史）。
 * ⚠️ 传**代表 id**（`item.id`）—— `done` 是逐条语义，合并条不展开。
 */
export function markReminderDone(id: string): Promise<ReminderDoneResultView> {
  return api<ReminderDoneResultView>(`/api/reminders/${encodeURIComponent(id)}/done`, { method: 'POST' });
}

// ═══════════════════ N4 注销（申请 / 状态 / 撤销） ═══════════════════

/**
 * 查注销状态。
 *
 * ⚠️ **只在"未申请"时调得通** —— 冷静期内 `AuthGuard` 对 `pending_deletion` 一律 403
 * （`N-O6`：冷静期不可登录写入）⇒ 那时剩余天数只能从**登录 403 的 `details`** 拿
 * （见 `LoginPage` 的冷静期分支）。
 */
export function getDeletionStatus(): Promise<AccountDeletionView> {
  return api<AccountDeletionView>('/api/settings/deletion');
}

/** 申请注销（二次输入**账号名** —— `AC-N4.24`；owner 仍有成员 ⇒ 409 带两条出路） */
export function requestDeletion(confirmName: string): Promise<AccountDeletionView> {
  return api<AccountDeletionView>('/api/settings/deletion', {
    method: 'POST',
    json: { confirmName },
  });
}

/**
 * 🔴 冷静期内**撤销注销**（`AC-N4.27`）。
 *
 * ⚠️ 它走 `auth` 且**不需要登录态** —— 申请人此刻已经被冻结（无可用 token），
 * 只能靠**邮箱 + 密码**重新证明身份。这也是"被冻结的人"唯一的出口。
 */
export function cancelDeletion(email: string, password: string): Promise<AccountDeletionView> {
  return api<AccountDeletionView>('/api/auth/cancel-deletion', {
    method: 'POST',
    json: { email, password },
  });
}

// ═══════════════════ N4 恢复（`§5.4.2` · 批次 `65` 端侧半边） ═══════════════════

/**
 * 恢复**预览**（`AC-N4.16`–`AC-N4.18` 的端侧消费者 · 🔴 **只见不动**）。
 *
 * ## 🔴 三条由**调用方**遵守的约束（都写在 `contract/restore.ts` 文件头）
 *
 * | 约束 | 不遵守会怎样 |
 * |---|---|
 * | 🔴 **"要不要二次确认"只读响应的 `requires`**，**不得**自己按 `mode` 推断 | `AC-N4.19` / `AC-N4.20` / `N-D2` 三条闸的判据在服务端；端侧自己写 `mode === 'overwrite'` = 抄第二份规则 ⇒ 服务端将来收紧时端侧**静默不跟** |
 * | 三项计数（新增 / 覆盖 / 冲突）**直接用**，禁再算一遍 | 口径在服务端（`conflicted ⊆ overwritten`）；端侧重算 = 第二份口径 |
 * | ⚠️ 警告的**判据是 `code`**，`message` 只是给人看的事实描述 | 按 `message` 文本分支 ⇒ 改一次文案就静默失效（契约已显式写死这条禁令） |
 *
 * ## 🔴 owner 闸（与导出**刻意不对称**）
 *
 * 两条恢复路由都挂 `@RequireOwner(PermissionAction.BulkOverwriteImport)` ——
 * 理由是"允许谁看恢复报告"与"允许谁恢复"应是**同一个答案**（controller 文件头原文）。
 * 导出则**所有成员可用**（导出永久免费）。⇒ 端侧入口应做 owner 可见性处理。
 *
 * ## ⚠️ 请求体是**整份备份**（可达 MB 级）
 *
 * 服务端为此在 `main.ts` 单独放宽了请求体上限（`RestorePolicy.MaxBodyBytes`）——
 * 这正是 `64` 批"改 `main.ts` 集成测试一行都盖不到"那个坑的落点。
 */
export function previewRestore(backup: unknown): Promise<RestorePreviewView> {
  return api<RestorePreviewView>('/api/settings/restore/preview', {
    method: 'POST',
    json: { backup },
  });
}

/**
 * 执行恢复（`AC-N4.19` / `AC-N4.20` / `AC-N4.22` · `N-O5` / `N-O10`）。
 *
 * 🔴 **四道闸在服务端**（缺一即 `400`）：缺父表拒恢复 · 缺关键表需显式确认 ·
 * `overwrite` 必须先导出 · `overwrite` 必须二次输入账本名。
 * ⇒ 端侧的处置是**把服务端 `message` 原样展示**，**不在端侧预判**（预判 = 第二份规则，
 * 且会让用户看不到服务端那条带**具体表名 / 账本名**的诊断）。
 *
 * ⚠️ `AC-N4.21`（覆盖前自动快照 + 可回滚）**本批不做**（需新载体 ⇒ 先走 DR）；
 * 兜底 = 强制前置导出（`exportedBeforehand` 是**确认旗标不是证据** —— 契约原文）。
 */
export function runRestore(body: RestoreRequest): Promise<RestoreResultView> {
  return api<RestoreResultView>('/api/settings/restore', { method: 'POST', json: body });
}

// ═══════════════════ N4 快照（`AC-N4.21` · 批次 `68` 端侧半边） ═══════════════════

/**
 * 列出本账本的快照还原点（`AC-N4.21` 的「**用户可见**」半边）。
 *
 * 🔴 **响应形状对照过 controller**（不是"看着像"）：
 * `setting.controller.ts:257` 的 `listSnapshots` 返回 `SnapshotListView` =
 * `{ items: SnapshotSummaryView[], keepLimit: number }`。
 * ⚠️ **不分页、也没有 `total`** —— 与 `/api/accounts`（`{items,total}`）和 `/api/transactions`
 * （`{items,page,pageSize,total,hasMore}`）**都不同形**。`api<T>` 的泛型是**自己填的**
 * ⇒ 类型写错 TS 不报错、运行时才 `undefined`（本项目已栽两次）。
 *
 * ⚠️ **owner 闸**：路由挂 `@RequireOwner(PermissionAction.BulkOverwriteImport)`
 * （与 `restore` 两条路由同闸）⇒ 非 owner 会 403。端侧入口须做可见性处理。
 */
export function listSnapshots(): Promise<SnapshotListView> {
  return api<SnapshotListView>('/api/settings/snapshots');
}

/**
 * 下载某份快照的**原始内容**（与当时导出的文件同内容）。
 *
 * 🔴 **走 `downloadAuthorized` 而不是 `api`** —— 响应是文件流
 * （`Content-Disposition: attachment`），需要 `fetch → blob → <a download>` 三步；
 * 直接用 `<a href>` 或 `api()` 都会因**不带 `Authorization` / `X-Ledger-Id`** 而失败。
 * 见 `api/download.ts` 文件头。
 *
 * ⚠️ 本函数只**拼路径**，动作在调用方（与两个导出接口同一分工）。
 */
export function snapshotDownloadPath(snapshotId: string): string {
  return `/api/settings/snapshots/${snapshotId}/download`;
}

/**
 * 回滚到某份快照（`AC-N4.21` 的「**可回滚**」半边）。
 *
 * 🔴 **本质是一次 `overwrite` 恢复**（`DR-039` §4）⇒ 返回的是既有的 `RestoreResultView`
 * （含逐账户余额 + 完整性三项）⇒ 结果区**直接复用** `RestoreDialog` 那套渲染口径，
 * 不另立一份"回滚报告"。
 *
 * ## 🔴 端侧**不预判**任何闸门
 *
 * 回滚服务端会走与覆盖**完全相同**的四道闸与核验（`DR-039` §4.2）。端侧的处置是
 * **把服务端 `message` 原样展示** —— 自己预判 = 第二份规则，且会让用户看不到
 * 服务端那条带**具体表名**的诊断。
 *
 * ⚠️ **唯一的端侧前置**是"点之前要二次确认"（`DR-039` D5：回滚 = 一次覆盖）——
 * 那条判据的出处见 `features/snapshot.ts` 文件头。
 */
export function rollbackSnapshot(body: RollbackSnapshotRequest): Promise<RestoreResultView> {
  return api<RestoreResultView>('/api/settings/snapshots/rollback', { method: 'POST', json: body });
}

// ─────────────────────── I 域：储蓄目标（`DR-050` · 批 85） ───────────────────────

/**
 * 目标列表 —— 🔴 **不分页**（目标与分类同量级，`AC-I1.3` 只要求 ≥5 个并存）。
 *
 * 🔴 返回里的 `progress` / `pace` / `milestones` **全是服务端算好的结论**
 * （`DR-023`：端侧金额聚合 = 0）—— 端侧不要自己再算一遍百分比。
 */
export function listGoals(): Promise<GoalListResult> {
  return api<GoalListResult>('/api/goals');
}

/** 单个目标（不存在 ⇒ 404） */
export function getGoal(id: string): Promise<GoalView> {
  return api<GoalView>(`/api/goals/${encodeURIComponent(id)}`);
}

/** 建目标（`accountId` / `dueDate` 都可以不给 —— `AC-I1.2`） */
export function createGoal(json: CreateGoalRequest): Promise<GoalView> {
  return api<GoalView>('/api/goals', { method: 'POST', json });
}

/** 改目标（🔴 **不发的键 = 不改**；服务端会拒"已有攒钱记录还换绑账户"） */
export function updateGoal(id: string, json: UpdateGoalRequest): Promise<GoalView> {
  return api<GoalView>(`/api/goals/${encodeURIComponent(id)}`, { method: 'PATCH', json });
}

/**
 * 删目标（`AC-I4.5`）。
 *
 * 🔴 `forfeit` = **放弃归属**：已有攒钱额时不带它服务端会 409（腿保留，但 `goal_id` 置空）
 * ⇒ 这一步必须由用户显式确认，不是端侧自动加。
 */
export function deleteGoal(id: string, forfeit: boolean): Promise<GoalDeletedView> {
  return api<GoalDeletedView>(
    `/api/goals/${encodeURIComponent(id)}?forfeit=${forfeit ? 'true' : 'false'}`,
    { method: 'DELETE' },
  );
}

/**
 * 攒一笔 / 取出（`AC-I2.1`–`I2.5`）。
 *
 * 🔴 请求体里**没有 `legs`** —— 腿结构在共享内核 `goalLegsOf()`（唯一实现），
 * 端侧只说"动作 + 金额 + 对手账户"。
 */
export function moveGoal(id: string, json: GoalMoveRequest): Promise<GoalMoveResultView> {
  return api<GoalMoveResultView>(`/api/goals/${encodeURIComponent(id)}/move`, {
    method: 'POST',
    json,
  });
}

/** 贡献流（时间倒序、有界；🔴 无排行 —— `AC-I3.4`） */
export function listGoalContributions(id: string): Promise<GoalContributionListResult> {
  return api<GoalContributionListResult>(
    `/api/goals/${encodeURIComponent(id)}/contributions?limit=100`,
  );
}

// ─────────────────────── D 域：账单导入（`DR-051` · 批 86） ───────────────────────

/**
 * 上传账单原文 → 预览（`AC-D1.1`：**不落库**）。
 *
 * 🔴 `file` **原样上传**（`Blob`），端侧**绝不**先读成文本 —— 微信 / 支付宝导出的 CSV
 * 常是 GBK，`FileReader.readAsText()` 默认按 UTF-8 解码，**乱码不可逆**
 * （这正是「导出后怎么全是问号」类投诉的来源）。编码识别是服务端内核的事
 * （`nucleus/import.ts` 的 `sniffEncoding`），端侧不起第二套判据。
 */
export function previewImport(
  file: Blob,
  query: { accountId: string; fileName?: string; encoding?: string },
): Promise<ImportPreviewResult> {
  const params = new URLSearchParams({ accountId: query.accountId });
  if (query.fileName !== undefined) params.set('fileName', query.fileName);
  if (query.encoding !== undefined) params.set('encoding', query.encoding);
  return apiUpload<ImportPreviewResult>(
    `/api/imports/preview?${params.toString()}`,
    file,
    'text/csv',
  );
}

/** 确认落库（`AC-D5.1` 整批原子）。`rows` **只带决策** —— 未列出的行用服务端建议 */
export function commitImport(json: ImportCommitRequest): Promise<ImportCommitResultView> {
  return api<ImportCommitResultView>('/api/imports', { method: 'POST', json });
}

/** 批次列表（`revocable` 由服务端算 —— 端侧**不判** 30 天窗口） */
export function listImportBatches(): Promise<ImportBatchListResult> {
  return api<ImportBatchListResult>('/api/imports');
}

/**
 * 撤销批次（`AC-D2.9` / `D2.10`）。
 *
 * 🔴 `force` **必须由用户确认后显式传** —— 服务端在有"手工改过"的记录时会回 **409 +
 * `details.editedCount`**，端侧据此弹二次确认（**不猜 `message` 文案**）。
 */
export function revokeImportBatch(batchId: string, force: boolean): Promise<ImportRevokedView> {
  return api<ImportRevokedView>(
    `/api/imports/${encodeURIComponent(batchId)}/revoke?force=${force ? 'true' : 'false'}`,
    { method: 'POST' },
  );
}

/** 规则列表（`AC-D3.6`） */
export function listImportRules(): Promise<ImportRuleListResult> {
  return api<ImportRuleListResult>('/api/import-rules');
}

/** 改规则（`AC-D3.7`）—— 改完立刻生效，无需重导 */
export function updateImportRule(id: string, json: UpdateImportRuleRequest): Promise<ImportRuleView> {
  return api<ImportRuleView>(`/api/import-rules/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    json,
  });
}

/** 删规则 */
export function deleteImportRule(id: string): Promise<ImportRuleDeletedView> {
  return api<ImportRuleDeletedView>(`/api/import-rules/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// ──────────────────── 发薪日工作流（F 域 F3 · `DR-052` · 批 87） ────────────────────

/**
 * 未分配的工资（**只读** · `AC-F3.1`：只提示，不自动启动）。
 *
 * 🔴 返回空数组是**正常状态**（没有新工资），不是错误。
 */
export function listPaydayCandidates(): Promise<PaydayCandidateListResult> {
  return api<PaydayCandidateListResult>('/api/paydays/candidates');
}

/**
 * 预演（`AC-F3.3` **只读**）。
 *
 * 🔴 **接受 `allocations`** —— 用户改完额度必须能立刻看到"改后的可花"（`AC-F3.4`），
 * 否则只能端侧自算（违反 `AC-P1.2`）或盲提交（铁律 94）。
 */
export function previewPayday(
  transactionId: string,
  allocations?: { goalId: string; amountCents: number }[],
): Promise<PaydayPreviewView> {
  return api<PaydayPreviewView>(
    `/api/paydays/${encodeURIComponent(transactionId)}/preview`,
    { method: 'POST', json: allocations === undefined ? {} : { allocations } },
  );
}

/** 落账（`AC-F3.5` 确认后才写；至少一项） */
export function confirmPayday(
  transactionId: string,
  allocations: { goalId: string; amountCents: number }[],
): Promise<PaydayConfirmResultView> {
  return api<PaydayConfirmResultView>(
    `/api/paydays/${encodeURIComponent(transactionId)}/confirm`,
    { method: 'POST', json: { allocations } },
  );
}

/** 分配历史（缺省近 90 天；只读） */
export function listPaydayHistory(): Promise<PaydayHistoryResult> {
  return api<PaydayHistoryResult>('/api/paydays/history');
}

// ─────────────────────────── R 域：重大资产（批 92 · `DR-053`） ───────────────────────────

/**
 * 列表（`AC-R4.2`）—— `items` **含隐藏项**（端侧自己折），`disposed` 单独一栏。
 *
 * 🔴 端侧**不在这里过滤**：过滤是展示决策（`features/major-asset.ts` 的 `groupAssets`），
 * 而"哪些资产存在"是服务端事实。
 */
export function listMajorAssets(): Promise<MajorAssetListResult> {
  return api<MajorAssetListResult>('/api/major-assets');
}

/** 详情（曲线 + 回报 + 关联贷款 + 净权益提示） */
export function getMajorAsset(id: string): Promise<MajorAssetDetailView> {
  return api<MajorAssetDetailView>(`/api/major-assets/${encodeURIComponent(id)}`);
}

/** 新建（最小必填 = 类型 + 名称 + 归属人） */
export function createMajorAsset(body: CreateMajorAssetRequest): Promise<MajorAssetDetailView> {
  return api<MajorAssetDetailView>('/api/major-assets', { method: 'POST', json: body });
}

/** 编辑（🔴 **整组替换** · `AC-R1.4`：调用方须预填全量） */
export function updateMajorAsset(
  id: string,
  body: UpdateMajorAssetRequest,
): Promise<MajorAssetDetailView> {
  return api<MajorAssetDetailView>(`/api/major-assets/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    json: body,
  });
}

/** 删除（级联 junction 与估值历史；交易保留但归因置空） */
export function deleteMajorAsset(id: string): Promise<MajorAssetDeletedView> {
  return api<MajorAssetDeletedView>(`/api/major-assets/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/** 关联贷款（整组替换 · 只能 `contra(loan)`） */
export function setMajorAssetLoans(
  id: string,
  body: SetMajorAssetLoansRequest,
): Promise<MajorAssetDetailView> {
  return api<MajorAssetDetailView>(`/api/major-assets/${encodeURIComponent(id)}/loans`, {
    method: 'POST',
    json: body,
  });
}

/** 调整估值（改值 + **写一条历史** · `AC-R7.1`） */
export function adjustMajorAssetValuation(
  id: string,
  body: AdjustValuationRequest,
): Promise<MajorAssetDetailView> {
  return api<MajorAssetDetailView>(`/api/major-assets/${encodeURIComponent(id)}/valuations`, {
    method: 'POST',
    json: body,
  });
}

/** 购入（一笔交易两条腿：首付 + 贷款本金 · `AC-R6.1`/`R6.2`） */
export function purchaseMajorAsset(
  id: string,
  body: PurchaseMajorAssetRequest,
): Promise<MajorAssetActionView> {
  return api<MajorAssetActionView>(`/api/major-assets/${encodeURIComponent(id)}/purchase`, {
    method: 'POST',
    json: body,
  });
}

/** 追加投资（不计收支 · 计入累计投入） */
export function appendMajorAssetInvestment(
  id: string,
  body: AppendInvestmentRequest,
): Promise<MajorAssetActionView> {
  return api<MajorAssetActionView>(`/api/major-assets/${encodeURIComponent(id)}/investments`, {
    method: 'POST',
    json: body,
  });
}

/** 记收益（计收入 · 分类二选一 · `AC-R6.4`） */
export function recordMajorAssetIncome(
  id: string,
  body: RecordIncomeRequest,
): Promise<MajorAssetActionView> {
  return api<MajorAssetActionView>(`/api/major-assets/${encodeURIComponent(id)}/incomes`, {
    method: 'POST',
    json: body,
  });
}

/** 处置（出售 / 报废 · 收款子笔不计收支 · 可一并结清贷款 · `AC-R8.1`–`R8.5`） */
export function disposeMajorAsset(
  id: string,
  body: DisposeMajorAssetRequest,
): Promise<MajorAssetActionView> {
  return api<MajorAssetActionView>(`/api/major-assets/${encodeURIComponent(id)}/dispose`, {
    method: 'POST',
    json: body,
  });
}
